import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  createServiceAccount,
  deleteServiceAccount,
  getServiceAccounts,
  updateServiceAccount,
  updateServiceAccountKeys,
} from "@/apis/service-account/service-account-apis";
import type {
  ServiceAccountCreate,
  ServiceAccountUpdate,
  ServiceAccountUpdateKeys,
} from "@/domain/service-account";
import { queryKeys } from "./keys";

export function useServiceAccounts() {
  return useQuery({
    queryKey: queryKeys.serviceAccounts.list(),
    queryFn: getServiceAccounts,
  });
}

export function useCreateServiceAccount() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (dto: ServiceAccountCreate) => createServiceAccount(dto),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.serviceAccounts.list(),
      });
    },
  });
}

export function useUpdateServiceAccount() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ key, dto }: { key: string; dto: ServiceAccountUpdate }) =>
      updateServiceAccount(key, dto),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.serviceAccounts.list(),
      });
    },
  });
}

export function useUpdateServiceAccountKeys() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      key,
      dto,
    }: {
      key: string;
      dto: ServiceAccountUpdateKeys;
    }) => updateServiceAccountKeys(key, dto),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.serviceAccounts.list(),
      });
    },
  });
}

export function useDeleteServiceAccount() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (key: string) => deleteServiceAccount(key),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.serviceAccounts.list(),
      });
    },
  });
}
